import React, { useState, useEffect } from 'react';
import api, { openDocument } from '../api';
import { format } from 'date-fns';
import { categoryLabel } from '../documentCategories';

const fmt = (v) => { try { return format(new Date(v), 'dd/MM/yyyy'); } catch { return ''; } };

// A staff member's own documents: contracts and letters the office has sent them
// (with signing where needed), plus anything they uploaded during onboarding.
// The company handbook and policies stay on the Company Documents page.
const MyDocuments = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [signDoc, setSignDoc] = useState(null);
  const [typedName, setTypedName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/api/documents/mine');
      setDocs(r.data);
    } catch (err) {
      setError('Failed to load your documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sent = docs.filter(d => d.scope === 'assigned');
  const uploads = docs.filter(d => d.scope === 'upload');
  const toSign = sent.filter(d => d.requires_signature && !d.signed);

  const view = async (id) => {
    try { await openDocument(id); }
    catch (err) { setError('Could not open that document'); }
  };

  const openSign = (doc) => { setSignDoc(doc); setTypedName(''); setAgreed(false); setError(''); setSuccess(''); };
  const closeSign = () => { setSignDoc(null); setError(''); };

  const confirmSign = async () => {
    if (signing) return;
    if (!typedName.trim()) return setError('Please type your full name');
    if (!agreed) return setError('Please tick the box to confirm');
    setSigning(true);
    try {
      await api.post(`/api/documents/${signDoc.id}/sign`, { typed_name: typedName.trim() });
      setError('');
      setSuccess(`${signDoc.title} signed`);
      setSignDoc(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sign');
    } finally {
      setSigning(false);
    }
  };

  const status = (d) => {
    if (!d.requires_signature) return null;
    return d.signed
      ? <span className="badge badge-approved">Signed</span>
      : <span className="badge badge-pending">Signature needed</span>;
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">My Documents</h1>
          <p className="page-subtitle">Your contract and any other documents the office has sent you.</p>
        </div>
      </div>

      {error && !signDoc && <div className="alert alert-error" onClick={() => setError('')}>{error}</div>}
      {success && <div className="alert alert-success" onClick={() => setSuccess('')}>{success}</div>}
      {toSign.length > 0 && (
        <div className="alert alert-info">
          {toSign.length === 1 ? '1 document is' : `${toSign.length} documents are`} waiting for your signature.
        </div>
      )}

      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header"><h3 className="card-title">Sent to you</h3></div>
        {loading ? (
          <div className="loader">Loading...</div>
        ) : sent.length === 0 ? (
          <div className="empty-state"><h3>No documents yet</h3><p>When the office sends you a contract or letter, it will appear here.</p></div>
        ) : (
          <div className="card-body" style={{ padding: 0 }}>
            {sent.map(d => (
              <div key={d.id} style={rowStyle}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 500, color: 'var(--navy)' }}>{d.title}</div>
                  <div style={hintStyle}>{categoryLabel(d.category)}, added {fmt(d.created_at)}</div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {status(d)}
                  <button className="btn btn-outline btn-sm" onClick={() => view(d.id)}>View</button>
                  {d.requires_signature && !d.signed && (
                    <button className="btn btn-primary btn-sm" onClick={() => openSign(d)}>Sign</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {uploads.length > 0 && (
        <div className="card">
          <div className="card-header"><h3 className="card-title">Documents you uploaded</h3></div>
          <div className="card-body" style={{ padding: 0 }}>
            {uploads.map(d => (
              <div key={d.id} style={rowStyle}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 500, color: 'var(--navy)' }}>{d.title}</div>
                  <div style={hintStyle}>{categoryLabel(d.category)}, added {fmt(d.created_at)}</div>
                </div>
                <button className="btn btn-outline btn-sm" onClick={() => view(d.id)}>View</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sign modal (same wording as onboarding) */}
      {signDoc && (
        <div className="modal-overlay" onClick={closeSign}>
          <div className="modal modal-scroll" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Sign document</h3>
              <button className="modal-close" onClick={closeSign}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <p style={{ color: 'var(--grey-mid)', fontSize: '13px', marginBottom: '16px' }}>
                <strong style={{ color: 'var(--navy)' }}>{signDoc.title}</strong>
              </p>
              <p style={{ color: 'var(--grey-mid)', fontSize: '13px', marginBottom: '16px' }}>
                Please read it first: <button type="button" className="btn btn-outline btn-sm" onClick={() => view(signDoc.id)}>View document</button>
              </p>
              <div className="form-group">
                <label className="form-label" htmlFor="sign-name">Type your full name to sign</label>
                <input id="sign-name" className="form-control" value={typedName} onChange={e => setTypedName(e.target.value)} placeholder="e.g. Jane Smith" />
              </div>
              <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer', fontSize: '13px', color: 'var(--navy)' }}>
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ accentColor: 'var(--red)', marginTop: '2px' }} />
                <span>I confirm I have read and agree to this document, and that typing my name counts as my signature.</span>
              </label>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={closeSign}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmSign} disabled={signing}>{signing ? 'Signing...' : 'Sign'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const rowStyle = { padding: '14px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' };
const hintStyle = { fontSize: '12px', color: 'var(--grey-mid)', marginTop: '2px' };

export default MyDocuments;
